import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ApplicationItem from 'components/AppicationItem';
import DeleteIcon from '@mui/icons-material/Delete';
import SentimentVeryDissatisfiedIcon from '@mui/icons-material/SentimentVeryDissatisfied';

import ConfirmModal from 'components/Modal/ConfirmModal';
import { api } from 'services/api';
import { formatDate } from 'utils/index';
import styles from './styles.module.scss';
import { getApplyBaseUrl } from '../../utils';

export type ApplicationStatus = 'ANALYSED' | 'HOLD' | 'REJECTED';
export type SortType = 'final_score' | 'test_score' | 'resume_score'; 

export interface Application {
  id: number;
  full_name: string;
  final_score: number;
  test_score: number;
  resume_score: number;
  is_viewed: boolean;
  email: string;
}

export interface DragItem {
  type: 'APPLICATION';
  application: Application;
  fromStatus: ApplicationStatus;
}

export interface CollectedProps {
  isOver: boolean;
  canDrop: boolean;
}

export interface ColumnData {
  applications: Application[];
  totalPages: number;
  total: number;
}

export default function VacancyPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [vacancy, setVacancy] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [columnSettings, setColumnSettings] = useState<Record<ApplicationStatus, { sortBy: SortType; page: number }>>({
    ANALYSED: { sortBy: 'final_score', page: 1 },
    HOLD: { sortBy: 'final_score', page: 1 },
    REJECTED: { sortBy: 'final_score', page: 1 },
  });
  const [expanded, setExpanded] = useState(false);

  const [columnData, setColumnData] = useState<Record<ApplicationStatus, ColumnData>>({
    ANALYSED: { applications: [], totalPages: 1, total: 0 },
    HOLD: { applications: [], totalPages: 1, total: 0 },
    REJECTED: { applications: [], totalPages: 1, total: 0 },
  });

  // Получаем базовый URL для приложений из переменных окружения
  const getApplyUrl = (slug: string) => {
    const baseUrl = getApplyBaseUrl();
    return `${baseUrl}/apply/${slug}`;
  };

  useEffect(() => {
    if (!id) return;
      api(`vacancies/${id}`)
        .then(res => {
          if (res.ok) {
            return res.json();
          }
        })
        .then(setVacancy)
        .catch(err => console.error('Ошибка при загрузке вакансии:', err))
        .finally(() => setLoading(false));
    }, [id]);

  // Устанавливаем заголовок страницы на основе названия вакансии
  useEffect(() => {
    if (vacancy && vacancy.title) {
      document.title = vacancy.title;
    }
  }, [vacancy]);

  const fetchApplications = useCallback(async (status: ApplicationStatus) => {
    if (!id) return;
    const { sortBy, page } = columnSettings[status];
    try {
      const res = await api(`vacancies/${id}/applications?status=${status}&sort=${sortBy}&page=${page}`);
      const data = await res.json();
      setColumnData(prev => ({
        ...prev,
        [status]: {
          applications: data.items,
          totalPages: data.totalPages,
          total: data.total,
        },
      }));
    } catch (err) {
      console.error(`Ошибка при загрузке заявок со статусом ${status}:`, err);
    }
  }, [id, columnSettings]);

  useEffect(() => {
    (['ANALYSED', 'HOLD', 'REJECTED'] as ApplicationStatus[]).forEach(status => {
      fetchApplications(status);
    });
  }, [fetchApplications]);

  const handleCopy = async () => {
    if (!vacancy?.apply_slug) return;
    try {
      const url = getApplyUrl(vacancy.apply_slug);
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Ошибка при копировании ссылки:', err);
    }
  };

  const handleStatusUpdate = async (
    applicationId: number,
    newStatus: ApplicationStatus,
    fromStatus: ApplicationStatus
  ) => {
    try {
      await api(`applications/${applicationId}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });
      // Обновляем обе колонки
      await Promise.all([
        fetchApplications(newStatus),
        fetchApplications(fromStatus),
      ]);
    } catch (err) {
      console.error('Ошибка при обновлении статуса:', err);
    }
  };

  const handleSortChange = (status: ApplicationStatus, newSort: SortType) => {
    setColumnSettings(prev => ({
      ...prev,
      [status]: { ...prev[status], sortBy: newSort, page: 1 },
    }));
    fetchApplications(status);
  };

  const handlePageChange = (status: ApplicationStatus, newPage: number) => {
    if (newPage < 1 || newPage > columnData[status].totalPages) return;
    setColumnSettings(prev => ({
      ...prev,
      [status]: { ...prev[status], page: newPage },
    }));
    fetchApplications(status);
  };

  const handleArchive = async () => {
    try {
      await api(`vacancies/${id}/deactivate`, { method: 'PATCH' });
      navigate('/vacancies'); // редирект после архивации
    } catch (err) {
      console.error('Ошибка при архивировании вакансии:', err);
    }
  };

  if (loading) return null;
  if (!vacancy || vacancy.statusCode === 404) return (
    <div className={styles.status}>
      <p>Вакансия не найдена</p>
      <SentimentVeryDissatisfiedIcon />
    </div>
  );

  return (
    <DndProvider backend={HTML5Backend}>
      <div className={styles.wrapper}>
        <div className={styles.breadcrumbs}><Link to='/vacancies'>← К списку вакансий</Link></div>
        <p className={styles.vacancyDate}>{formatDate(vacancy.created_at)}</p>
        <div className={styles.titleWrapper}>
          <h2 className={styles.title}>
            {vacancy.title}
            <span className={styles.vacancyGrade}>{vacancy.grade}</span>
          </h2>
          {vacancy.is_active && (
            <button
              className={styles.archiveButton}
              onClick={() => setShowModal(true)}
            >
              <DeleteIcon />
              Отправить в архив
            </button>
          )}
        </div>

        <div className={styles.linkBoxWrapper}>
          Ссылка для откликов:&nbsp;
          <div className={styles.linkBox}>
            <span className={styles.linkText}>
              {getApplyUrl(vacancy.apply_slug)}
            </span>
            <span className={styles.copyIcon} onClick={handleCopy}>
              {copied ? <CheckCircleIcon style={{ color: 'green' }} /> : <ContentCopyIcon />}
            </span>
          </div>
        </div>

      <div
        className={`${styles.descriptionWrapper}`}
      >
        <h3>Описание вакансии</h3>
        <p className={`${styles.description} ${expanded ? styles.expanded : ''}`}>
          {vacancy.description}
        </p>
        <span className={styles.toggleText} onClick={() => setExpanded(prev => !prev)}>
          {expanded ? 'Свернуть' : 'Показать больше'}
        </span>
      </div>

        <div className={styles.board}>
          {(['ANALYSED', 'HOLD', 'REJECTED'] as const).map(status => (
            <ApplicationItem
              key={status}
              status={status}
              applications={columnData[status].applications}
              sortBy={columnSettings[status].sortBy}
              page={columnSettings[status].page}
              totalPages={columnData[status].totalPages}
              onSortChange={handleSortChange}
              onPageChange={handlePageChange}
              onDropApplication={handleStatusUpdate}
            />
          ))}
        </div>
      </div>
      {showModal && (
        <ConfirmModal
          title="Архивировать вакансию?"
          description="Вы уверены, что хотите архивировать эту вакансию? Отменить это действие невозможно. Возобновить прием откликов по этой вакансии будет нельзя"
          onConfirm={handleArchive}
          onCancel={() => setShowModal(false)}
        />
      )}
    </DndProvider>
  );
}
